import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Factory, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  contact_person: "",
  phone: "",
  email: "",
  gst: "",
  address: "",
  region: "",
  product_categories: "",
  plant_capacity: "",
  kiln_types: "",
  tile_sizes: "",
  quality_grades: "",
  lead_time_days: "",
  notes: "",
};

export default function VendorPlants() {
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/distributor/vendor-plants");
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load plants");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Plant name is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        contact_person: form.contact_person.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        gst: form.gst.trim() || null,
        address: form.address.trim() || null,
        region: form.region.trim() || null,
        product_categories: form.product_categories.trim() || null,
        plant_capacity: form.plant_capacity.trim() || null,
        kiln_types: form.kiln_types.trim() || null,
        tile_sizes: form.tile_sizes.trim() || null,
        quality_grades: form.quality_grades.trim() || null,
        lead_time_days: form.lead_time_days ? Number(form.lead_time_days) : null,
        notes: form.notes.trim() || null,
      };
      await api.post("/distributor/vendor-plants", payload);
      toast.success(`${form.name.trim()} added`);
      setAddOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add plant");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete plant "${r.name}"?`)) return;
    try {
      await api.delete(`/distributor/vendor-plants/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.name || "").toLowerCase().includes(s)
      || (r.region || "").toLowerCase().includes(s)
      || (r.product_categories || "").toLowerCase().includes(s);
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="vendor-plants">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2"><Factory className="w-7 h-7 text-orange-600" /> Vendor plants</h1>
          <p className="text-muted-foreground">Manufacturing plants and factories the distributor sources tiles from — capacity, kiln types, sizes, grades and lead times.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input placeholder="Search plant, region, category…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" data-testid="plants-search" />
          <Button onClick={() => { setForm(EMPTY); setAddOpen(true); }} data-testid="plants-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add plant
          </Button>
        </div>
      </div>

      <Card><CardContent className="p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Plant</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Region</TableHead>
            <TableHead>Categories</TableHead>
            <TableHead>Capacity</TableHead>
            <TableHead>Tile sizes</TableHead>
            <TableHead>Kilns</TableHead>
            <TableHead>Grades</TableHead>
            <TableHead>Lead time</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`plant-row-${r.id}`}>
                <TableCell>
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{r.gst || ""}</div>
                </TableCell>
                <TableCell>
                  <div className="text-xs font-semibold">{r.contact_person || "—"}</div>
                  <div className="text-[10px] text-muted-foreground">{r.phone || ""}</div>
                  <div className="text-[10px] text-muted-foreground">{r.email || ""}</div>
                </TableCell>
                <TableCell>{r.region || "—"}</TableCell>
                <TableCell className="text-xs max-w-[180px] truncate" title={r.product_categories || ""}>{r.product_categories || "—"}</TableCell>
                <TableCell className="text-xs">{r.plant_capacity || "—"}</TableCell>
                <TableCell className="text-xs max-w-[160px] truncate" title={r.tile_sizes || ""}>{r.tile_sizes || "—"}</TableCell>
                <TableCell className="text-xs">{r.kiln_types || "—"}</TableCell>
                <TableCell className="text-xs">{r.quality_grades || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px] font-mono">{r.lead_time_days ? `${r.lead_time_days}d` : "—"}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => remove(r)} data-testid={`plant-del-${r.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-10">No vendor plants yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-3xl" data-testid="plants-add-dialog">
          <DialogHeader><DialogTitle>Add vendor plant</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label htmlFor="plant-name">Plant / manufacturer name *</Label>
              <Input id="plant-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plant-name" placeholder="Claymont Ceramics – Unit II" />
            </div>
            <div>
              <Label htmlFor="plant-contact">Contact person</Label>
              <Input id="plant-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="plant-contact" placeholder="Rakesh Patel" />
            </div>
            <div>
              <Label htmlFor="plant-region">Region</Label>
              <Input id="plant-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="plant-region" placeholder="Morbi, Gujarat" />
            </div>
            <div>
              <Label htmlFor="plant-phone">Phone</Label>
              <Input id="plant-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="plant-phone" placeholder="+91-98xxxxxxxx" />
            </div>
            <div>
              <Label htmlFor="plant-email">Email</Label>
              <Input id="plant-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="plant-email" placeholder="plant@example.com" />
            </div>
            <div>
              <Label htmlFor="plant-gst">GST number</Label>
              <Input id="plant-gst" value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} data-testid="plant-gst" placeholder="24AAAAA1234A1Z5" />
            </div>
            <div>
              <Label htmlFor="plant-lead">Lead time (days)</Label>
              <Input id="plant-lead" type="number" min="0" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} data-testid="plant-lead" placeholder="15" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="plant-cats">Product categories</Label>
              <Input id="plant-cats" value={form.product_categories} onChange={(e) => setForm({ ...form, product_categories: e.target.value })} data-testid="plant-cats" placeholder="GVT, PVT, Wall tiles, Parking" />
            </div>
            <div>
              <Label htmlFor="plant-cap">Plant capacity</Label>
              <Input id="plant-cap" value={form.plant_capacity} onChange={(e) => setForm({ ...form, plant_capacity: e.target.value })} data-testid="plant-cap" placeholder="50,000 sqm / day" />
            </div>
            <div>
              <Label htmlFor="plant-kiln">Kiln types</Label>
              <Input id="plant-kiln" value={form.kiln_types} onChange={(e) => setForm({ ...form, kiln_types: e.target.value })} data-testid="plant-kiln" placeholder="Roller, Tunnel" />
            </div>
            <div>
              <Label htmlFor="plant-sizes">Tile sizes produced</Label>
              <Input id="plant-sizes" value={form.tile_sizes} onChange={(e) => setForm({ ...form, tile_sizes: e.target.value })} data-testid="plant-sizes" placeholder="600x600, 800x800, 1200x1200" />
            </div>
            <div>
              <Label htmlFor="plant-grades">Quality grades</Label>
              <Input id="plant-grades" value={form.quality_grades} onChange={(e) => setForm({ ...form, quality_grades: e.target.value })} data-testid="plant-grades" placeholder="Premium, Standard, Commercial" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="plant-address">Address</Label>
              <Input id="plant-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="plant-address" placeholder="Plot 45, Ceramic Park, Morbi — 363641" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="plant-notes">Notes</Label>
              <Input id="plant-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="plant-notes" placeholder="Payment terms, MOQ, quality history…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="plant-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="plant-submit">{saving ? "Saving…" : "Add plant"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
